import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { Wine, Leaf, GlassWater, Landmark, Snowflake, CheckCircle2, Heart } from "lucide-react";
import { Header, Footer } from "../components/Chrome";
import { useSettings } from "../lib/settings";
import { api, formatError, resolveImage } from "../lib/api";
import { useLiveFetch } from "../lib/useLiveFetch";
import { toast } from "sonner";

const EASE_ED = [0.76, 0, 0.24, 1];
const EASE_S = [0.16, 1, 0.3, 1];

// Convenience-store snack merchandising image (replaces fruits/veg)
const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=1920";

function HeroLine({ children, delay = 0 }) {
  return (
    <span className="hero-mask">
      <motion.span className="hero-line"
        initial={{ y: "110%" }} animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: EASE_ED }}>
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const { settings } = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroImg = settings?.homepage_hero_image || DEFAULT_HERO_IMAGE;
  const headline = settings?.homepage_headline || "Your Trusted Convenience Wholesale Partner";
  const parts = headline.split(" ");
  const line1 = parts.slice(0, 2).join(" ");
  const line2 = parts.slice(2, 4).join(" ");
  const line3 = parts.slice(4).join(" ");

  return (
    <section ref={ref} className="relative min-h-[100vh] pt-24 md:pt-28 overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={heroImg} alt="Convenience wholesale snacks and beverages" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-paper/70 mix-blend-lighten" />
        <div className="absolute inset-0 grain" />
      </motion.div>

      <div className="relative z-10 px-6 md:px-12 lg:px-16 pt-8 md:pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE_S }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="w-2 h-2 bg-soda animate-pulse" />
          <span className="overline">Est. GTA · Ontario · Canada</span>
        </motion.div>

        <h1 className="font-display text-[13vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.88] uppercase tracking-tighter">
          <HeroLine delay={0.2}>{line1 || "Your Trusted"}</HeroLine><br/>
          <HeroLine delay={0.35}>{line2 || "Convenience Wholesale"}</HeroLine><br/>
          <HeroLine delay={0.5}>{line3 || <em className="font-editorial italic tracking-normal">partner.</em>}</HeroLine>
        </h1>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: EASE_S }}
            className="md:col-span-6 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            {settings?.homepage_subheadline || "Beverages, snacks, chocolate, candy, energy, protein and imported reserve — delivered to convenience stores, gas stations, cafeterias and grab-and-go outlets across the Greater Toronto Area."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: EASE_S }}
            className="md:col-span-5 md:col-start-8 flex flex-col sm:flex-row gap-3"
          >
            {settings?.enquiry_form_enabled && (
              <Link to="/enquiry" className="btn-primary" data-testid="hero-enquiry-btn">Request pricing →</Link>
            )}
            <Link to="/catalog" className="btn-ghost" data-testid="hero-catalog-btn">Browse catalog</Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const { settings } = useSettings();
  const items = (settings?.announcements && settings.announcements.length > 0)
    ? settings.announcements
    : ["Beverages", "Chips", "Chocolates", "Candies", "Energy Drinks", "Protein Bars", "Imported Reserve", "Grab & Go"];
  const speed = settings?.marquee_speed || 40;
  return (
    <section className="bg-ink text-paper border-y border-ink py-6 md:py-10 overflow-hidden" data-testid="marquee-section">
      <Marquee speed={speed} gradient={false} pauseOnHover={false}>
        {items.concat(items).map((it, i) => (
          <span key={i} className="font-display text-4xl md:text-7xl uppercase leading-none px-6 md:px-8 flex items-center gap-6 md:gap-8">
            {it}<span className="text-soda">✱</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function Chapter({ number, title, body, image, imageLeft = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-black/10" data-testid={`chapter-${number}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9, ease: EASE_S }}
          className={`lg:col-span-5 ${imageLeft ? "lg:order-2" : ""}`}
        >
          <div className="overline text-black/50 mb-6">Chapter · {number}</div>
          <h3 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] uppercase tracking-tight">{title}</h3>
          <p className="mt-8 text-lg leading-relaxed max-w-lg">{body}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1, ease: EASE_ED }}
          className={`lg:col-span-7 ${imageLeft ? "lg:order-1" : ""} relative`}
        >
          <div className="relative aspect-[4/3] overflow-hidden border border-ink hard-shadow">
            <motion.img src={image} alt="" style={{ y }} className="w-full h-[115%] object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Reference image showing the 4 signature Banta bottles (Nimbu, Masala, Blueberry, Jeera)
const BANTA_HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_snack-supply-hub/artifacts/zzsxp7kf_WhatsApp%20Image%202026-07-23%20at%204.52.51%20PM.jpeg";

const BANTA_FLAVOURS = [
  { name: "Nimbu Pani",       en: "Citron Épicé",     notes: "Zesty · Refreshing · Classic", color: "#8BB93D" },
  { name: "Masala Pineapple", en: "Ananas Épicé",     notes: "Sweet · Spicy · Tropical",     color: "#F08A1D" },
  { name: "Blueberry",        en: "Bleuet",           notes: "Bold · Fruity · Refreshing",   color: "#2E7CC2" },
  { name: "Jeera Shikanji",   en: "Citron au Cumin",  notes: "Tangy · Spiced · Digestive",   color: "#9A5B33" },
];

const BANTA_FEATURES = [
  { icon: Wine,       title: "Premium Extra Codd Soda",   sub: "Marble-stopper glass bottle" },
  { icon: Leaf,       title: "Made With Quality Ingredients", sub: "Authentic recipes, real flavour" },
  { icon: GlassWater, title: "Glass Bottle, Premium Feel", sub: "Elevates any shelf or fridge" },
  { icon: Landmark,   title: "Authentic Indian Nostalgia", sub: "Born in India. Made for Canada" },
];

const BANTA_TRUST = [
  { icon: "🇮🇳",         label: "Made in India" },
  { icon: "🍁",         label: "Imported to Canada" },
  { icon: CheckCircle2, label: "CFIA Compliant" },
  { icon: Snowflake,    label: "Best Served Chilled" },
  { icon: Heart,        label: "Loved by All Ages" },
];

function BantaSpotlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  return (
    <section
      ref={ref}
      className="relative bg-ink text-paper overflow-hidden border-t border-white/10"
      data-testid="banta-spotlight"
    >
      {/* Ambient background — warm spotlights */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full blur-[120px] opacity-25" style={{ background: "radial-gradient(circle, #F08A1D 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px] opacity-20" style={{ background: "radial-gradient(circle, #2E7CC2 0%, transparent 70%)" }} />
      </div>
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative z-10 px-6 md:px-12 lg:px-16 py-20 md:py-28 lg:py-32">
        {/* Overline + Wordmark */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-2 h-2 bg-soda animate-pulse rounded-full" />
              <span className="overline text-white/60">03 · Imported Reserve · Signature Range</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: EASE_S }}
              className="font-display text-[18vw] md:text-[13vw] lg:text-[11vw] leading-[0.8] uppercase tracking-tighter"
              data-testid="banta-wordmark"
            >
              BANTA
            </motion.h2>
            <div className="mt-3 font-editorial italic text-2xl md:text-3xl lg:text-4xl text-soda">
              Apna Desi Drink~
            </div>
            <div className="mt-2 overline text-white/50 text-[11px] tracking-[0.32em]">
              · Premium Extra Codd Soda · Born in India. Made for Canada 🍁
            </div>
          </div>
          <div className="hidden lg:flex flex-col items-end shrink-0">
            <div className="border border-white/20 rounded-full px-6 py-4 text-center" style={{ borderStyle: "double" }}>
              <div className="overline text-white/50 text-[9px] tracking-widest">Inspired by India</div>
              <div className="font-display text-2xl leading-none mt-1">EST · 2024</div>
              <div className="overline text-white/50 text-[9px] tracking-widest mt-1">Made for Canada</div>
            </div>
          </div>
        </div>

        {/* Main 3-column: pitch · image · features */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left pitch */}
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9, ease: EASE_S }}
            className="lg:col-span-3 order-2 lg:order-1"
          >
            <div className="w-12 h-[3px] bg-[#F08A1D] mb-4" />
            <div className="overline text-white/60 mb-3">Premium Indian Beverage</div>
            <h3 className="font-display text-3xl md:text-4xl uppercase leading-[0.95] tracking-tight">
              Nostalgic <span className="text-[#F08A1D]">flavours.</span><br/>
              Modern experience.
            </h3>
            <p className="mt-5 text-white/70 text-sm md:text-base leading-relaxed">
              A perfect blend of authentic Indian taste and premium quality. The marble-stopper Codd-neck soda — opened with a satisfying pop.
            </p>
            <Link
              to="/banta"
              className="mt-8 inline-flex items-center gap-2 btn-primary"
              style={{ background: "#E63946", borderColor: "#E63946" }}
              data-testid="banta-page-btn"
            >
              Explore Banta →
            </Link>
          </motion.div>

          {/* Center hero image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1.1, ease: EASE_ED }}
            className="lg:col-span-6 order-1 lg:order-2"
          >
            <div className="relative border border-white/15 overflow-hidden hard-shadow-inv bg-black">
              <motion.img
                src={BANTA_HERO_IMG}
                alt="Banta premium Codd-neck sodas — Nimbu Pani, Masala Pineapple, Blueberry, Jeera Shikanji"
                style={{ y: yImg }}
                className="w-full h-auto object-contain"
                data-testid="banta-hero-image"
              />
              {/* Bottom-left EST tag on mobile */}
              <div className="lg:hidden absolute -bottom-3 -left-3 bg-paper text-ink px-3 py-1.5 font-display text-xl">
                EST · 2024
              </div>
            </div>
          </motion.div>

          {/* Right features */}
          <motion.ul
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9, ease: EASE_S }}
            className="lg:col-span-3 order-3 space-y-4"
          >
            {BANTA_FEATURES.map(({ icon: Icon, title, sub }, i) => (
              <li key={title} className="flex items-start gap-3 border-l-2 border-[#F08A1D]/70 pl-4 py-1">
                <Icon className="w-5 h-5 mt-0.5 text-[#F08A1D] shrink-0" strokeWidth={1.6} />
                <div>
                  <div className="font-bold text-sm uppercase tracking-wide leading-tight">{title}</div>
                  <div className="text-xs text-white/55 mt-0.5">{sub}</div>
                </div>
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Flavour row */}
        <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="banta-flavour-row">
          {BANTA_FLAVOURS.map((fl, i) => (
            <motion.div
              key={fl.name}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE_S }}
              className="group border-t-2 pt-4"
              style={{ borderColor: fl.color }}
              data-testid={`banta-flavour-${i}`}
            >
              <div className="font-display text-xl md:text-2xl uppercase leading-none" style={{ color: fl.color }}>
                {fl.name}
              </div>
              <div className="font-editorial italic text-white/60 text-sm mt-1">{fl.en}</div>
              <div className="overline text-white/40 text-[10px] mt-3 tracking-[0.22em]">{fl.notes}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Trust bar — deep green matching the reference footer strip */}
      <div className="relative z-10 border-t border-white/10" style={{ background: "#0d3d2e" }} data-testid="banta-trust-bar">
        <div className="px-6 md:px-12 lg:px-16 py-5 md:py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {BANTA_TRUST.map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-paper/90">
              {typeof icon === "string"
                ? <span className="text-lg leading-none">{icon}</span>
                : (() => { const Icon = icon; return <Icon className="w-4 h-4 text-[#F5D77A]" strokeWidth={2} />; })()}
              <span className="overline text-[10px] md:text-[11px] tracking-[0.22em] text-paper/90">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoriesPreview() {
  const { data: catsData } = useLiveFetch("/categories/public?section=regular", { initial: [] });
  const cats = (catsData || []).slice(0, 8);
  return (
    <section className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-black/10" data-testid="categories-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
        <div className="lg:col-span-5">
          <div className="overline text-black/50 mb-6">02 · The Convenience Aisle</div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tighter">
            The whole<br/>convenience aisle.
          </h2>
        </div>
        <p className="lg:col-span-5 lg:col-start-8 text-lg leading-relaxed self-end">
          Trusted brands moving fast — plus a curated shelf of imports your competitors don't stock. Tap any aisle to explore the catalogue.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {cats.map((c, i) => (
          <motion.a
            key={c.id} href={`/catalog?category=${c.slug}`}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE_S }}
            className="group relative aspect-[3/4] border border-ink overflow-hidden hover:hard-shadow"
            data-testid={`category-${c.slug}`}
            style={{ transition: "box-shadow 250ms ease" }}
          >
            {c.image_url ? <img src={resolveImage(c.image_url)} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /> : <div className="w-full h-full bg-secondary" />}
            {c.status === "coming_soon" && (
              <div className="absolute top-3 right-3 bg-soda text-paper px-3 py-1 text-[10px] font-bold uppercase tracking-widest">Coming soon</div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-paper/95 border-t border-ink flex items-center justify-between">
              <span className="font-display text-xl md:text-2xl uppercase tracking-tight">{c.name}</span>
              <span className="overline">→</span>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
}

const CUSTOMERS = ["Convenience Stores", "Gas Stations", "Hospital Gift Shops",
  "Independent Retailers", "Cafeterias", "Grab-and-Go Outlets"];

function ServiceArea() {
  const { settings } = useSettings();
  const cur = settings?.currency || "$";
  const amt = settings?.free_delivery_amount ?? 500;
  return (
    <section id="about" className="bg-ink text-paper py-20 md:py-32 border-t border-white/10" data-testid="service-section">
      <div className="px-6 md:px-12 lg:px-16">
        <div className="overline text-white/50 mb-6">06 · Who we serve</div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <ul className="lg:col-span-8 space-y-2 lg:space-y-4">
            {CUSTOMERS.map((c, i) => (
              <motion.li key={c}
                initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE_S }}
                className="border-b border-white/15 pb-3 flex items-baseline gap-4 md:gap-6"
              >
                <span className="overline text-white/40 w-10 shrink-0">0{i+1}</span>
                <span className="font-display text-3xl md:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">{c}</span>
              </motion.li>
            ))}
          </ul>
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="overline text-white/50 mb-4">Service Area</div>
            <p className="text-white/70 leading-relaxed">
              Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Oakville, Burlington, Hamilton, Ajax, Pickering, Whitby, Oshawa — plus surrounding Ontario on request.
            </p>
            <div className="mt-10 border border-white/25 p-6" data-testid="delivery-block">
              <div className="overline text-white/50 mb-2">Delivery & Pickup</div>
              <div className="font-display text-2xl md:text-3xl leading-none mb-2">FREE OVER {cur}{Number(amt).toLocaleString()}</div>
              <p className="text-sm text-white/70 leading-relaxed">{settings?.delivery_info || "Same-week delivery across the GTA. Curbside pickup available at our Scarborough warehouse."}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { data: reviewsData } = useLiveFetch("/reviews/public", { initial: [] });
  const reviews = reviewsData || [];
  if (reviews.length === 0) return null;
  return (
    <section className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-black/10" data-testid="testimonials-section">
      <div className="overline text-black/50 mb-6">Praise</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {reviews.map((t, i) => (
          <motion.figure key={t.id}
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.1, ease: EASE_S }}
            className="border border-ink p-6 md:p-8 flex flex-col justify-between min-h-[260px]"
            data-testid={`review-${t.id}`}
          >
            <blockquote className="font-editorial text-xl md:text-2xl lg:text-3xl italic leading-tight">"{t.text}"</blockquote>
            <figcaption className="mt-6 overline text-black/60">{t.name}</figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

function ContactForm() {
  const { settings } = useSettings();
  const [f, setF] = useState({ name: "", email: "", phone: "", business: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", f);
      toast.success(`Message sent — reference #${data.inquiry_no}. We'll respond within 1 business day.`);
      setF({ name: "", email: "", phone: "", business: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <section id="contact" className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-black/10" data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline text-black/50 mb-6">07 · Get in touch</div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tighter">
            Talk to us.
          </h2>
          <div className="mt-10 space-y-4 text-lg">
            {settings?.show_phone && settings?.phone && <div><span className="overline text-black/50 block mb-1">Phone</span>{settings.phone}</div>}
            {settings?.show_email && settings?.public_email && <div><span className="overline text-black/50 block mb-1">Email</span>{settings.public_email}</div>}
            {settings?.hours && <div><span className="overline text-black/50 block mb-1">Hours</span>{settings.hours}</div>}
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-6 lg:col-start-7 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input required placeholder="Full name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="field" data-testid="contact-name" />
            <input required type="email" placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="field" data-testid="contact-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="field" data-testid="contact-phone" />
            <input placeholder="Business" value={f.business} onChange={e => setF({ ...f, business: e.target.value })} className="field" data-testid="contact-business" />
          </div>
          <textarea required rows={5} placeholder="How can we help?" value={f.message} onChange={e => setF({ ...f, message: e.target.value })} className="field" data-testid="contact-message" />
          <button type="submit" disabled={busy} className="btn-primary w-full md:w-auto" data-testid="contact-submit">
            {busy ? "Sending…" : "Send message →"}
          </button>
        </form>
      </div>
    </section>
  );
}

function EnquiryCTA() {
  const { settings } = useSettings();
  if (!settings?.enquiry_form_enabled) return null;
  return (
    <section className="relative bg-paper border-t border-ink overflow-hidden" data-testid="enquiry-cta">
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="overline mb-6">Ready to stock?</div>
        <Link to="/enquiry" className="block group" data-testid="footer-enquiry-link">
          <div className="font-display text-[15vw] md:text-[13vw] lg:text-[12vw] uppercase leading-[0.85] tracking-tighter transition-colors group-hover:text-cobalt">
            Request<br/>wholesale pricing →
          </div>
        </Link>
        <p className="mt-8 max-w-lg text-lg">Send us your business info and what you're looking to stock. We'll reply with pricing and availability within one business day.</p>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="bg-paper text-ink">
      <Header />
      <Hero />
      <EditorialMarquee />
      <Chapter
        number="01" title="The Supply."
        body="Beverages, chips, chocolates, candies, protein bars, energy drinks, cookies, and everyday convenience essentials — the trusted brands your customers already ask for, priced for retailers, ready by the pallet."
        image="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=1400"
      />
      <CategoriesPreview />
      <BantaSpotlight />
      <Chapter
        number="04" title="The Logistics."
        body="Same-week delivery across the GTA and surrounding Ontario. Curbside pickup at our Scarborough warehouse for anyone who prefers to grab and go."
        image="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1400"
        imageLeft
      />
      <Chapter
        number="05" title="The Service."
        body="Real people on the phone. Same-day quotes. A distribution partner that treats independent retailers like the businesses they are."
        image="https://images.unsplash.com/photo-1553413077-190dd305871c?w=1400"
      />
      <ServiceArea />
      <Testimonials />
      <ContactForm />
      <EnquiryCTA />
      <Footer />
    </div>
  );
}
